import React, { useState, useRef } from 'react';
import {
  Upload,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Download,
  FileText,
  Info,
  FileSpreadsheet,
  Trash2,
  Eye,
} from 'lucide-react';
import { useGenerateCard30File } from '../hooks/useReports';

const RECORD_30_SPEC = [
  { no: 1, field: 'Record Type', format: '9(2)', position: '1-2', length: 2, required: 'Yes', description: 'Always equals "30"' },
  { no: 2, field: 'Action Type', format: '9(2)', position: '3-4', length: 2, required: 'Yes', description: '01-new card/register, 02-update, 03-dependent/additional' },
  { no: 3, field: 'First Name', format: 'X(50)', position: '5-54', length: 50, required: 'Yes', description: 'First name of cardholder' },
  { no: 4, field: 'Middle Initial', format: 'X(1)', position: '55-55', length: 1, required: 'Opt', description: 'Middle initial of cardholder' },
  { no: 5, field: 'Last Name', format: 'X(50)', position: '56-105', length: 50, required: 'Yes', description: 'Last name of cardholder' },
  { no: 6, field: 'Suffix', format: 'X(3)', position: '106-108', length: 3, required: 'Opt', description: 'Suffix of cardholder' },
  { no: 7, field: 'SSN', format: '9(9)', position: '109-117', length: 9, required: 'Yes', description: 'Social Security Number' },
  { no: 8, field: 'Mailing Addr1', format: 'X(50)', position: '118-167', length: 50, required: 'Yes', description: 'Current mailing address line 1' },
  { no: 9, field: 'Mailing Addr2', format: 'X(50)', position: '168-217', length: 50, required: 'Opt', description: 'Current mailing address line 2' },
  { no: 10, field: 'Mailing City', format: 'X(35)', position: '218-252', length: 35, required: 'Yes', description: 'Current mailing city' },
  { no: 11, field: 'Mailing State', format: 'X(25)', position: '253-277', length: 25, required: 'Yes', description: 'State (2-char abbreviation)' },
  { no: 12, field: 'Mailing Postal Code', format: 'X(30)', position: '278-307', length: 30, required: 'Yes', description: 'Current mailing postal code' },
  { no: 13, field: 'Mailing Country Code', format: '9(3)', position: '308-310', length: 3, required: 'Yes', description: 'ISO code (USA uses 840)' },
  { no: 14, field: 'Home Number', format: '9(23) or X(23)', position: '311-333', length: 23, required: 'Yes', description: 'Home phone number (must be > 0)' },
  { no: 15, field: 'Delivery Method', format: '9(2)', position: '334-335', length: 2, required: 'Yes', description: '01-first class, 02-next day air, etc.' },
  { no: 16, field: 'PAN/Proxy/Client Unique ID', format: '9(19) or X(19)', position: '336-354', length: 19, required: 'Cond', description: 'Card identifier (PAN or Proxy)' },
  { no: 17, field: 'Filler', format: 'X(3)', position: '355-357', length: 3, required: 'Yes', description: 'Filler spaces' },
  { no: 18, field: 'Card Record Status Code', format: '9(2)', position: '358-359', length: 2, required: 'Yes', description: '01-sent (always provide in input)' },
  { no: 19, field: 'Processing Message', format: 'X(41)', position: '360-400', length: 41, required: 'FIS', description: 'Message from FIS processing' },
];

const EXPECTED_COLUMNS = [
  'Member ID',
  'Tenant Name',
  'Address 1',
  'Address 2',
  'City',
  'State',
  'Zip Code',
];

const DELIVERY_METHODS = [
  { value: '01', label: '01 - First Class Mail' },
  { value: '02', label: '02 - Next Day Air' },
  { value: '03', label: '03 - Second Day Air' },
  { value: '04', label: '04 - Standard Mail' },
];

export default function FISCard30() {
  const [uploadedData, setUploadedData] = useState(null);
  const [fileName, setFileName] = useState('');
  const [uploadError, setUploadError] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [actionType, setActionType] = useState('01');
  const [testProdIndicator, setTestProdIndicator] = useState('P');
  const fileInputRef = useRef(null);
  const { mutate, data, isPending, isError, error, isSuccess, reset } = useGenerateCard30File();

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadError(null);
    setPreviewData(null);
    reset();

    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
    ];
    const ext = file.name.split('.').pop().toLowerCase();
    if (!validTypes.includes(file.type) && !['xlsx', 'xls', 'csv'].includes(ext)) {
      setUploadError('Please upload an Excel file (.xlsx, .xls) or CSV file (.csv)');
      return;
    }

    try {
      const XLSX = await import('xlsx');
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

      if (jsonData.length === 0) {
        setUploadError('The uploaded file contains no data rows.');
        return;
      }

      const fileColumns = Object.keys(jsonData[0]);
      const missingColumns = EXPECTED_COLUMNS.filter(
        (col) => !fileColumns.some((fc) => fc.trim().toLowerCase() === col.toLowerCase())
      );

      if (missingColumns.length > 0) {
        setUploadError(
          `Missing required columns: ${missingColumns.join(', ')}. Expected columns: ${EXPECTED_COLUMNS.join(', ')}`
        );
        return;
      }

      const normalizedData = jsonData.map((row) => {
        const normalized = {};
        EXPECTED_COLUMNS.forEach((expectedCol) => {
          const matchingKey = Object.keys(row).find(
            (k) => k.trim().toLowerCase() === expectedCol.toLowerCase()
          );
          normalized[expectedCol] = matchingKey ? String(row[matchingKey]).trim() : '';
        });
        return normalized;
      });

      setUploadedData(normalizedData);
      setFileName(file.name);
    } catch (err) {
      setUploadError(`Failed to parse file: ${err.message}`);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClearUpload = () => {
    setUploadedData(null);
    setFileName('');
    setUploadError(null);
    setPreviewData(null);
    reset();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePreviewData = () => {
    if (uploadedData && uploadedData.length > 0) {
      setPreviewData(null);
      mutate(
        { members: uploadedData, preview: true, actionType, testProdIndicator },
        {
          onSuccess: (response) => {
            if (response?.data) {
              setPreviewData(response.data);
            }
          },
        }
      );
    }
  };

  const handleDeliveryMethodChange = (index, newValue) => {
    setPreviewData((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], delivery_method: newValue };
      return updated;
    });
  };

  const handleGenerateFile = () => {
    // If we have edited preview data, send it with delivery_method overrides
    const membersToSend = previewData
      ? uploadedData.map((m, idx) => ({
          ...m,
          delivery_method: previewData[idx]?.delivery_method || '01',
        }))
      : uploadedData;

    if (membersToSend && membersToSend.length > 0) {
      mutate(
        { members: membersToSend, preview: false, actionType, testProdIndicator },
        {
          onSuccess: (response) => {
            if (response?.fileContent) {
              const blob = new Blob([response.fileContent], { type: 'text/plain' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `FIS_Card30_${actionType === '01' ? 'New' : 'Update'}_${new Date().toISOString().split('T')[0]}.txt`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            }
          },
        }
      );
    }
  };

  const specColumns = [
    { key: 'no', label: 'No' },
    { key: 'field', label: 'Field Name' },
    { key: 'format', label: 'Format' },
    { key: 'position', label: 'Position' },
    { key: 'length', label: 'Length' },
    { key: 'required', label: 'Required' },
  ];

  const previewColumns = [
    { key: 'member_id', label: 'Member ID' },
    { key: 'tenant_name', label: 'Tenant Name' },
    { key: 'action_type', label: 'Action' },
    { key: 'first_name', label: 'First Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'ssn', label: 'SSN' },
    { key: 'mailing_addr1', label: 'Address 1' },
    { key: 'mailing_addr2', label: 'Address 2' },
    { key: 'mailing_city', label: 'City' },
    { key: 'mailing_state', label: 'State' },
    { key: 'mailing_postal_code', label: 'Zip Code' },
    { key: 'home_number', label: 'Phone' },
    { key: 'delivery_method', label: 'Delivery Method' },
    { key: 'pan_proxy', label: 'PAN/Proxy' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">FIS Card 30 Generator</h2>
        <p className="text-gray-500 mt-1">
          Generate Generic Batch Loader files with Record Type 30 (Issue Record: Required Cardholder Data)
        </p>
      </div>

      <div className="flex items-start gap-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div>
          <h4 className="font-medium text-amber-900">Record Type 30 Specification</h4>
          <p className="text-sm text-amber-700 mt-1">
            This generates a fixed-width (non-delimited) text file with 400-character records.
            Each record contains required cardholder data including name, address, SSN, and delivery information for FIS batch processing.
          </p>
        </div>
      </div>

      {/* Field Specification */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Field Specification</h3>
        </div>
        <div className="overflow-x-auto max-h-64 overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                {specColumns.map((col) => (
                  <th
                    key={col.key}
                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {RECORD_30_SPEC.map((spec) => (
                <tr key={spec.no} className="hover:bg-gray-50">
                  <td className="px-4 py-2 whitespace-nowrap">{spec.no}</td>
                  <td className="px-4 py-2 whitespace-nowrap font-medium">{spec.field}</td>
                  <td className="px-4 py-2 whitespace-nowrap font-mono text-xs">{spec.format}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{spec.position}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{spec.length}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        spec.required === 'Yes'
                          ? 'bg-red-100 text-red-700'
                          : spec.required === 'Cond'
                          ? 'bg-yellow-100 text-yellow-700'
                          : spec.required === 'FIS'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {spec.required}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Action Type Selection */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <Info className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Action Type</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Select whether this batch is for new card issuance or updating existing cardholder records.
        </p>
        <div className="flex gap-4">
          <label
            className={`flex-1 flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
              actionType === '01'
                ? 'border-amber-500 bg-amber-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="actionType"
              value="01"
              checked={actionType === '01'}
              onChange={(e) => {
                setActionType(e.target.value);
                setPreviewData(null);
              }}
              className="w-4 h-4 text-amber-600"
            />
            <div>
              <p className="font-medium text-gray-900">01 - New Issuance</p>
              <p className="text-sm text-gray-500">
                Register new cardholder. PAN/Proxy will be set to all zeros.
              </p>
            </div>
          </label>
          <label
            className={`flex-1 flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
              actionType === '02'
                ? 'border-amber-500 bg-amber-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="actionType"
              value="02"
              checked={actionType === '02'}
              onChange={(e) => {
                setActionType(e.target.value);
                setPreviewData(null);
              }}
              className="w-4 h-4 text-amber-600"
            />
            <div>
              <p className="font-medium text-gray-900">02 - Update Existing</p>
              <p className="text-sm text-gray-500">
                Update existing cardholder. PAN/Proxy will use the value from the database.
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* Test/Prod Indicator */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <Info className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Environment</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Select the target environment for file processing. Use Test for UAT validation before sending to Production.
        </p>
        <div className="flex gap-4">
          <label
            className={`flex-1 flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
              testProdIndicator === 'P'
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="testProd"
              value="P"
              checked={testProdIndicator === 'P'}
              onChange={(e) => setTestProdIndicator(e.target.value)}
              className="w-4 h-4 text-green-600"
            />
            <div>
              <p className="font-medium text-gray-900">P - Production</p>
              <p className="text-sm text-gray-500">Full processing required</p>
            </div>
          </label>
          <label
            className={`flex-1 flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
              testProdIndicator === 'T'
                ? 'border-yellow-500 bg-yellow-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="testProd"
              value="T"
              checked={testProdIndicator === 'T'}
              onChange={(e) => setTestProdIndicator(e.target.value)}
              className="w-4 h-4 text-yellow-600"
            />
            <div>
              <p className="font-medium text-gray-900">T - Test</p>
              <p className="text-sm text-gray-500">Test file format only (UAT)</p>
            </div>
          </label>
        </div>
      </div>

      {/* Excel Upload Section */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <FileSpreadsheet className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Upload Member Data</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Upload an Excel file (.xlsx, .xls) or CSV with the following columns:
          <span className="font-medium text-gray-700 ml-1">
            {EXPECTED_COLUMNS.join(', ')}
          </span>
        </p>

        {!uploadedData ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
            <div className="flex flex-col items-center justify-center text-center">
              <Upload className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-sm text-gray-600 mb-2">
                Drag and drop your Excel file here, or click to browse
              </p>
              <p className="text-xs text-gray-400 mb-4">
                Supports .xlsx, .xls, and .csv files
              </p>
              <label className="btn-primary cursor-pointer">
                <Upload className="w-4 h-4 mr-2" />
                Choose File
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-900">{fileName}</p>
                  <p className="text-sm text-green-700">{uploadedData.length} member records loaded</p>
                </div>
              </div>
              <button
                onClick={handleClearUpload}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Clear
              </button>
            </div>
          </div>
        )}

        {uploadError && (
          <div className="mt-4 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{uploadError}</p>
          </div>
        )}

        {/* Uploaded Data Preview */}
        {uploadedData && uploadedData.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Uploaded Data Preview (first {Math.min(uploadedData.length, 10)} of {uploadedData.length} rows)
            </h4>
            <div className="overflow-x-auto max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    {EXPECTED_COLUMNS.map((col) => (
                      <th
                        key={col}
                        className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {uploadedData.slice(0, 10).map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-400">{idx + 1}</td>
                      {EXPECTED_COLUMNS.map((col) => (
                        <td key={col} className="px-4 py-2 whitespace-nowrap">
                          {row[col] || <span className="text-gray-300">-</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {uploadedData && (
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-2 text-sm">
              {isPending && (
                <span className="flex items-center gap-2 text-amber-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </span>
              )}
              {isSuccess && data?.rowCount !== undefined && (
                <span className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="w-4 h-4" />
                  {previewData
                    ? `Preview: ${data.rowCount} records resolved`
                    : `File generated with ${data.rowCount} records`}
                </span>
              )}
              {isSuccess && data?.errors && data.errors.length > 0 && (
                <span className="flex items-center gap-2 text-orange-600">
                  <AlertCircle className="w-4 h-4" />
                  {data.errors.length} member(s) could not be resolved
                </span>
              )}
              {isError && (
                <span className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  {error?.response?.data?.message || error?.message || 'Operation failed'}
                </span>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={handlePreviewData}
                disabled={isPending}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview Data
              </button>
              <button
                onClick={handleGenerateFile}
                disabled={isPending}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4 mr-2" />
                Generate & Download File
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Resolved Data Preview with editable delivery method */}
      {previewData && previewData.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Resolved Card 30 Data</h3>
            <div className="flex items-center gap-3">
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                testProdIndicator === 'T' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
              }`}>
                {testProdIndicator === 'T' ? 'TEST' : 'PRODUCTION'}
              </span>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                actionType === '01' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'
              }`}>
                {actionType === '01' ? 'New Issuance' : 'Update Existing'}
              </span>
              <span className="text-sm text-gray-500">{previewData.length} records</span>
            </div>
          </div>
          <p className="text-sm text-gray-500 mb-3">
            You can change the <span className="font-medium">Delivery Method</span> for each record before generating the file.
          </p>
          <div className="overflow-x-auto max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  {previewColumns.map((col) => (
                    <th
                      key={col.key}
                      className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {previewData.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-400">{idx + 1}</td>
                    {previewColumns.map((col) => (
                      <td key={col.key} className="px-3 py-2 whitespace-nowrap">
                        {col.key === 'delivery_method' ? (
                          <select
                            value={row.delivery_method || '01'}
                            onChange={(e) => handleDeliveryMethodChange(idx, e.target.value)}
                            className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                          >
                            {DELIVERY_METHODS.map((dm) => (
                              <option key={dm.value} value={dm.value}>
                                {dm.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          row[col.key] || <span className="text-gray-300">-</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Errors for unresolved members */}
      {isSuccess && data?.errors && data.errors.length > 0 && (
        <div className="card border-orange-200">
          <h3 className="text-lg font-semibold text-orange-800 mb-4">
            Unresolved Members ({data.errors.length})
          </h3>
          <div className="overflow-x-auto max-h-48 overflow-y-auto border border-orange-200 rounded-lg">
            <table className="min-w-full divide-y divide-orange-200 text-sm">
              <thead className="bg-orange-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-orange-600 uppercase">Member ID</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-orange-600 uppercase">Tenant Name</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-orange-600 uppercase">Reason</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-orange-100">
                {data.errors.map((err, idx) => (
                  <tr key={idx} className="hover:bg-orange-50">
                    <td className="px-4 py-2 font-medium">{err.memberId}</td>
                    <td className="px-4 py-2">{err.tenantName}</td>
                    <td className="px-4 py-2 text-red-600">{err.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Sample Output */}
      {isSuccess && data?.sampleLines && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sample Output (Header + First 3 Data Lines)</h3>
          <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
            <pre className="text-green-400 font-mono text-xs whitespace-pre">
              {data.sampleLines.join('\n')}
            </pre>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Each line is exactly 400 characters (fixed-width format)
          </p>
        </div>
      )}
    </div>
  );
}
